import React, { useState, useEffect, useRef } from 'react';
import { EditProjectForm } from './EditProjectForm';
import { ProjectCard } from './ProjectCard';
import '../../../styles/resume-editor.css';

interface Project {
  uid: string;
  title: string;
  headline: string;
  link?: string;
  linkAlias?: string;
  description?: string;
  releaseDate?: string | null;
  yearOnly?: boolean;
  hide?: boolean;
  achievements?: string[];
}

interface ProjectsSectionProps {
  initialProjects?: Project[];
  onProjectsChange?: (projects: Project[]) => void;
  editingProjectUid?: string | null;
  onEditingProjectUidChange?: (uid: string | null) => void;
  onProjectDataChange?: (uid: string, data: Project) => void;
  onProjectSave?: (project: Project, isNew: boolean) => Promise<void>;
  onProjectDelete?: (uid: string) => Promise<void>;
  onProjectsReorder?: (projects: Project[]) => Promise<void>;
  cvUid?: string;
}

export const ProjectsSection: React.FC<ProjectsSectionProps> = ({
  initialProjects = [],
  onProjectsChange,
  editingProjectUid: externalEditingProjectUid,
  onEditingProjectUidChange,
  onProjectDataChange,
  onProjectSave,
  onProjectDelete,
  onProjectsReorder,
  cvUid,
}) => {
  const [projects, setProjects] = useState<Project[]>(initialProjects);
  const [internalEditingProjectUid, setInternalEditingProjectUid] = useState<string | null>(null);
  const [draggedProjectIndex, setDraggedProjectIndex] = useState<number | null>(null);
  const [dragOverProjectIndex, setDragOverProjectIndex] = useState<number | null>(null);
  const prevInitialProjectsRef = useRef<string>('');
  
  const editingProjectUid = externalEditingProjectUid !== undefined ? externalEditingProjectUid : internalEditingProjectUid;
  const setEditingProjectUid = (uid: string | null) => {
    if (onEditingProjectUidChange) {
      onEditingProjectUidChange(uid);
    } else {
      setInternalEditingProjectUid(uid);
    }
  };

  useEffect(() => {
    const newUids = initialProjects.map(p => p.uid).sort().join(',');
    if (prevInitialProjectsRef.current !== newUids) {
      prevInitialProjectsRef.current = newUids;
      setProjects(initialProjects);
    }
  }, [initialProjects]);

  useEffect(() => {
    onProjectsChange?.(projects);
  }, [projects, onProjectsChange]);

  const handleReorderAchievements = (uid: string, newAchievements: string[]) => {
    setProjects(projects.map(p => 
      p.uid === uid ? { ...p, achievements: newAchievements } : p
    ));
  };

  return (
    <div className="resume-editor__section-expanded-body">
      {editingProjectUid ? (
        <EditProjectForm
          key={editingProjectUid}
          project={editingProjectUid === 'new' ? {
            uid: 'new',
            title: '',
            headline: '',
            link: '',
            linkAlias: '',
            description: '',
            releaseDate: null,
            yearOnly: false,
            hide: false,
            achievements: []
          } : projects.find(p => p.uid === editingProjectUid)!}
          cvUid={cvUid}
          onDataChange={(updatedProject) => {
            if (onProjectDataChange && editingProjectUid) {
              onProjectDataChange(editingProjectUid, updatedProject);
            }
          }}
          onSave={async (updatedProject) => {
            const isNew = updatedProject.uid === 'new';
            if (onProjectSave) {
              await onProjectSave(updatedProject, isNew);
            } else {
              if (isNew) {
                const newUid = `project-${Date.now()}`;
                setProjects([...projects, { ...updatedProject, uid: newUid }]);
              } else {
                setProjects(projects.map(p => 
                  p.uid === updatedProject.uid ? updatedProject : p
                ));
              }
            }
            setEditingProjectUid(null);
            if (onProjectDataChange) {
              onProjectDataChange(updatedProject.uid, updatedProject);
            }
          }}
          onCancel={() => {
            if (onProjectDataChange && editingProjectUid && editingProjectUid !== 'new') {
              const originalProject = projects.find(p => p.uid === editingProjectUid);
              if (originalProject) {
                onProjectDataChange(editingProjectUid, originalProject);
              }
            }
            setEditingProjectUid(null);
          }}
        />
      ) : (
        <>
          <button 
            className="resume-editor__add-experience-btn"
            onClick={() => setEditingProjectUid('new')}
          >
            + Add Project
          </button>
          {projects.length > 0 && (
            <div className="resume-editor__experience-list">
              {projects.map((project, index) => (
                <div
                  key={project.uid}
                  draggable="true"
                  onDragStart={(e) => {
                    setDraggedProjectIndex(index);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    setDragOverProjectIndex(index);
                  }}
                  onDragLeave={() => {
                    setDragOverProjectIndex(null);
                  }}
                  onDrop={async (e) => {
                    e.preventDefault();
                    if (draggedProjectIndex !== null && draggedProjectIndex !== dragOverProjectIndex && dragOverProjectIndex !== null) {
                      const newProjects = [...projects];
                      const [removed] = newProjects.splice(draggedProjectIndex, 1);
                      newProjects.splice(dragOverProjectIndex, 0, removed);
                      setProjects(newProjects);
                      if (onProjectsReorder) {
                        await onProjectsReorder(newProjects);
                      }
                    }
                    setDraggedProjectIndex(null);
                    setDragOverProjectIndex(null);
                  }}
                  onDragEnd={() => {
                    setDraggedProjectIndex(null);
                    setDragOverProjectIndex(null);
                  }}
                  className={`resume-editor__experience-card-wrapper ${draggedProjectIndex === index ? 'resume-editor__experience-card-wrapper--dragging' : ''} ${dragOverProjectIndex === index ? 'resume-editor__experience-card-wrapper--drag-over' : ''}`}
                >
                  <ProjectCard
                    project={project}
                    cvUid={cvUid}
                    onEdit={(project) => {
                      setEditingProjectUid(project.uid);
                    }}
                    onDelete={async (uid) => {
                      if (onProjectDelete) {
                        await onProjectDelete(uid);
                      } else {
                        setProjects(projects.filter(p => p.uid !== uid));
                      }
                    }}
                    onReorderAchievements={handleReorderAchievements}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

